// frontend/src/app/pages/profile/account-card/account-card.component.ts
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AccountDeletionPreview,
  SettingsService,
} from '../../../services/settings.service';
import { AuthService } from '../../../services/auth.service';
import { AnalyticsService } from '../../../services/analytics.service';

/** « Mon compte » dans « Mon profil » (BUG-04, D-08) : pause de l'abonnement et suppression du compte. */
@Component({
  selector: 'app-account-card',
  imports: [ReactiveFormsModule, RouterLink, DatePipe],
  templateUrl: './account-card.component.html',
})
export class AccountCardComponent {
  private readonly settingsService = inject(SettingsService);
  private readonly authService = inject(AuthService);
  private readonly analytics = inject(AnalyticsService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  /** null tant que l'aperçu n'est pas chargé */
  readonly preview = signal<AccountDeletionPreview | null>(null);
  readonly confirming = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');

  readonly deleteForm = this.fb.nonNullable.group({
    password: ['', [Validators.required]],
    code: ['', [Validators.maxLength(20)]],
    confirmed: [false, [Validators.requiredTrue]],
  });

  constructor() {
    this.settingsService
      .getAccountDeletionPreview()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.preview.set(res.data ?? null),
        error: () =>
          this.error.set('Informations du compte indisponibles. Réessayez.'),
      });
  }

  startDeletion(): void {
    const codeControl = this.deleteForm.controls.code;
    codeControl.setValidators(
      this.preview()?.twoFactorRequired
        ? [Validators.required, Validators.maxLength(20)]
        : [Validators.maxLength(20)],
    );
    codeControl.updateValueAndValidity();
    this.error.set('');
    this.confirming.set(true);
  }

  cancel(): void {
    this.confirming.set(false);
    this.error.set('');
    this.deleteForm.reset();
  }

  confirmDeletion(): void {
    if (this.deleteForm.invalid) {
      this.deleteForm.markAllAsTouched();
      return;
    }
    const { password, code } = this.deleteForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.settingsService
      .deleteAccount(password, code.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.analytics.trackCompteSupprime();
          this.authService.logout({ deleted: 'true' });
        },
        error: (err: HttpErrorResponse) => {
          this.busy.set(false);
          this.error.set(
            err.error?.message ?? 'Suppression impossible. Réessayez.',
          );
        },
      });
  }
}
